import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { Plus, Search } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import { ActionButton, Button, Pagination } from '../../components/UI';

interface ClienteItem {
  _id: string;
  nome: string;
  email?: string;
  telefone?: string;
  status: 'lead' | 'negociacao' | 'cliente_ativo' | 'inativo';
  responsavelId?: { name: string };
  createdAt: string;
}

const STATUS_VALUES = ['lead', 'negociacao', 'cliente_ativo', 'inativo'];

const STATUS_LABELS: { [key: string]: string } = {
  lead: 'Lead',
  negociacao: 'Em negociação',
  cliente_ativo: 'Cliente ativo',
  inativo: 'Inativo'
};

const STATUS_COLORS: { [key: string]: string } = {
  lead: 'bg-gray-100 text-gray-800',
  negociacao: 'bg-blue-100 text-blue-800',
  cliente_ativo: 'bg-green-100 text-green-800',
  inativo: 'bg-red-100 text-red-800'
};

interface ClientesProps {
  embedded?: boolean;
}

const Clientes: React.FC<ClientesProps> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const [clientes, setClientes] = useState<ClienteItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const notifications = useNotifications();
  const confirm = useConfirm();

  const fetchClientes = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...(searchTerm && { search: searchTerm }),
        ...(selectedStatus && { status: selectedStatus }),
      });

      const response = await axios.get(`/clientes?${params}`);
      setClientes(response.data.clientes);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedStatus, currentPage]);

  useEffect(() => {
    fetchClientes();
  }, [fetchClientes]);

  const handleDelete = async (clienteId: string) => {
    if (!(await confirm({ message: t('admin.clientesDeleteConfirm'), confirmText: t('common.delete'), variant: 'danger' }))) return;
    try {
      await axios.delete(`/clientes/${clienteId}`);
      setClientes(clientes.filter((cliente) => cliente._id !== clienteId));
      notifications.success.clienteDeleted();
    } catch (error: any) {
      console.error('Erro ao excluir cliente:', error);
      notifications.error.clienteDelete(error.response?.data?.message || 'Erro ao excluir cliente');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Ações da linha: mesma marcação na tabela (desktop) e nos cards (mobile).
  const renderActions = (cliente: (typeof clientes)[number]) => (
    <div className="row-actions md:justify-end">
      <ActionButton
        action="view"
        label="Ver ficha"
        to={`/admin/clientes/${cliente._id}`}
      />
      <ActionButton
        action="edit"
        to={`/admin/clientes/${cliente._id}/edit`}
      />
      <ActionButton
        action="delete"
        onClick={() => handleDelete(cliente._id)}
      />
    </div>
  );

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gray-50'}>
      {/* Header */}
      {!embedded && (
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            <div className="mb-4">
              <Breadcrumbs />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{t('admin.clientes')}</h1>
                <p className="text-gray-600">{t('admin.clientesDesc')}</p>
              </div>
              <Button to="/admin/clientes/new">
                <Plus className="w-5 h-5" />
                <span>{t('admin.clienteNew')}</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8'}>
        {embedded && (
          <div className="flex justify-end mb-4">
            <Button to="/admin/clientes/new">
              <Plus className="w-5 h-5" />
              <span>{t('admin.clienteNew')}</span>
            </Button>
          </div>
        )}
        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder={t('admin.clientesSearchPh')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="field pl-10 pr-4"
                />
              </div>
            </div>

            <div className="lg:w-56">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="field field--select"
              >
                <option value="">Todos os status</option>
                {STATUS_VALUES.map((status) => (
                  <option key={status} value={status}>
                    {STATUS_LABELS[status]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">{t('common.loading')}</p>
            </div>
          ) : clientes.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-gray-400 mb-4">
                <Search className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('admin.clientesEmpty')}</h3>
              <p className="text-gray-600 mb-4">Comece cadastrando seu primeiro cliente.</p>
              <Button to="/admin/clientes/new">
                Cadastrar Primeiro Cliente
              </Button>
            </div>
          ) : (
            <>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Responsável</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Criado em</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {clientes.map((cliente) => (
                    <tr key={cliente._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/admin/clientes/${cliente._id}`} className="text-sm font-medium text-primary-600 hover:text-primary-800">
                          {cliente.nome}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{cliente.email || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{cliente.telefone || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[cliente.status]}`}>
                          {STATUS_LABELS[cliente.status]}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cliente.responsavelId?.name || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(cliente.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {renderActions(cliente)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="md:hidden divide-y divide-gray-200">
              {clientes.map((cliente) => (
                <li key={cliente._id} className="p-4 space-y-3">
                  <div className="min-w-0">
                    <Link to={`/admin/clientes/${cliente._id}`} className="text-sm font-medium text-primary-600 hover:text-primary-800">
                      {cliente.nome}
                    </Link>
                  </div>
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Email</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {cliente.email || '—'}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Telefone</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {cliente.telefone || '—'}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Status</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[cliente.status]}`}>
                          {STATUS_LABELS[cliente.status]}
                        </span>
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Responsável</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {cliente.responsavelId?.name || '—'}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Criado em</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {formatDate(cliente.createdAt)}
                      </dd>
                    </div>
                  </dl>
                  {renderActions(cliente)}
                </li>
              ))}
            </ul>
            </>
          )}

          <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setCurrentPage} />
        </div>
      </div>
    </div>
  );
};

export default Clientes;
